import { ThreadsEntrySchema } from "./schema-BYZYkgA1.js";
import { formatAvailableThreadList, formatThreadStacktraceOutput } from "./formatting-Cviy8-dX.js";

//#region src/tools/support/event-stacktrace.ts
/**
* Fetches an issue event and formats the selected thread stacktrace.
*/
async function fetchAndFormatEventStacktrace({ apiService, organizationSlug, issueId, eventId, thread }) {
	const event = await apiService.getEventForIssue({
		organizationSlug,
		issueId,
		eventId
	});
	const threads = getThreads(event);
	let output = `# Event Stacktrace in **${organizationSlug}**\n\n`;
	output += `**Issue ID**: ${issueId}\n`;
	output += `**Event ID**: ${event.id}\n\n`;
	if (threads.length === 0) {
		output += "No thread stacktraces were found for this event.\n";
		return output;
	}
	const selected = selectThread(threads, thread);
	if (selected.kind !== "selected") {
		output += `${selected.message}\n\n`;
		output += formatAvailableThreadList(threads);
		return output;
	}
	output += formatThreadStacktraceOutput({
		event,
		thread: selected.thread,
		selectionReason: selected.reason
	});
	return output;
}
function getThreads(event) {
	const threadsEntry = event.entries?.find((entry) => entry.type === "threads");
	const result = ThreadsEntrySchema.safeParse(threadsEntry?.data);
	return result.success ? result.data.values ?? [] : [];
}
function selectThread(threads, selector) {
	if (selector === void 0) {
		const selected = selectSentryDefaultThread(threads);
		return {
			kind: "selected",
			thread: selected,
			reason: "Sentry default selection: first crashed thread, then first thread with a stacktrace, then first thread."
		};
	}
	if (typeof selector === "number") return selectUnique(threads, (thread) => String(thread.id) === String(selector), `No thread found with ID ${selector}.`, `Multiple threads matched ID ${selector}.`);
	const nameMatch = selectUnique(threads, (thread) => thread.name === selector, "", `Multiple threads matched name "${selector}".`);
	if (nameMatch.kind !== "not_found") return nameMatch;
	if (/^-?\d+$/.test(selector)) return selectUnique(threads, (thread) => String(thread.id) === selector, `No thread found with name or ID "${selector}".`, `Multiple threads matched ID ${selector}.`);
	return {
		kind: "not_found",
		message: `No thread found with name "${selector}".`
	};
}
function selectSentryDefaultThread(threads) {
	const sortedThreads = [...threads].sort((a, b) => Number(Boolean(b.crashed)) - Number(Boolean(a.crashed)));
	return sortedThreads.find((thread) => thread.crashed) ?? sortedThreads.find((thread) => hasFrames(thread)) ?? sortedThreads[0];
}
function hasFrames(thread) {
	return Boolean(thread.stacktrace?.frames?.length);
}
function selectUnique(threads, predicate, notFoundMessage, ambiguousMessage) {
	const matches = threads.filter(predicate);
	if (matches.length === 0) return {
		kind: "not_found",
		message: notFoundMessage
	};
	if (matches.length > 1) return {
		kind: "ambiguous",
		message: ambiguousMessage
	};
	return {
		kind: "selected",
		thread: matches[0],
		reason: "Matched the provided thread selector."
	};
}

//#endregion
export { fetchAndFormatEventStacktrace };
//# sourceMappingURL=event-stacktrace-DNVGqkZt.js.map